import { Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { McpServer } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  useGrantMcpPermission,
  useMcpPermissions,
  useRevokeMcpPermission,
} from "@/features/mcps/hooks/useMcps";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  mcp: McpServer;
}

export function McpPermissionsTab({ mcp }: Props) {
  const { data: perms, isLoading } = useMcpPermissions(mcp.id);
  const { data: teams } = useTeams({ include_inactive: false });
  const grant = useGrantMcpPermission(mcp.id);
  const revoke = useRevokeMcpPermission(mcp.id);
  const canManage = useCan(Action.MCP_SERVER_PERMISSION_GRANTED, {
    owner_team_id: mcp.owner_team_id,
  });

  const [pickedTeam, setPickedTeam] = useState("");

  const grantedIds = new Set([
    mcp.owner_team_id,
    ...(perms?.map((p) => p.team_id) ?? []),
  ]);
  const available = teams?.filter((t) => !grantedIds.has(t.id)) ?? [];
  const ownerTeam = teams?.find((t) => t.id === mcp.owner_team_id);

  async function handleGrant() {
    if (!pickedTeam) return;
    try {
      await grant.mutateAsync({ team_id: pickedTeam, permission: "use" });
      toast.success("Permissão concedida");
      setPickedTeam("");
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleRevoke(teamId: string) {
    try {
      await revoke.mutateAsync(teamId);
      toast.success("Permissão revogada");
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="space-y-4 py-4">
      <div className="rounded-md border p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-medium">{ownerTeam?.name ?? "Owner"}</span>
          <Badge variant="outline">Owner (use implícito)</Badge>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : null}

      {perms && perms.length > 0 ? (
        <ul className="divide-y rounded-md border">
          {perms.map((p) => (
            <li
              key={p.team_id}
              className="flex items-center justify-between px-3 py-2"
            >
              <span className="text-sm font-medium">{p.team_name}</span>
              {canManage ? (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRevoke(p.team_id)}
                  disabled={revoke.isPending}
                  aria-label="Revogar"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : !isLoading ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma permissão concedida.
        </p>
      ) : null}

      {canManage ? (
        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-semibold">Conceder ``use`` a um time</h3>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="mcp-pick-team">Time</Label>
              <select
                id="mcp-pick-team"
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                value={pickedTeam}
                onChange={(e) => setPickedTeam(e.target.value)}
              >
                <option value="">Selecione</option>
                {available.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleGrant}
              disabled={!pickedTeam || grant.isPending}
            >
              Conceder
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
